import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CPDRecognitionApplyPage() {
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    website: '',
    program_name: '',
    program_type: '',
    learning_hours: '',
    target_audience: '',
    program_description: '',
    learning_objectives: '',
    recognition_level: 'level_1',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.organization_name || !formData.email || !formData.program_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/cpd-recognition/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Application submitted successfully');
      } else {
        toast.error('Failed to submit application. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="cpd-recognition-apply-page">
        <section className="bg-[#1B2A4A] py-16 md:py-20">
          <div className="imcac-container">
            <p className="imcac-overline mb-3">CPD Recognition</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Apply for Recognition</h1>
            <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          </div>
        </section>
        <section className="imcac-section bg-white">
          <div className="imcac-container max-w-2xl text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-3">Application Submitted</h2>
            <p className="text-base text-slate-600 mb-6">
              Thank you for your application. Our team will review your submission and contact you within 10 business days.
            </p>
            <p className="text-sm text-slate-500">Reference: CPD-{Date.now().toString(36).toUpperCase()}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="cpd-recognition-apply-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Apply for Recognition</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Independent organizations may submit their educational activities for IMCAC CPD Recognition.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-3xl">
          <form onSubmit={handleSubmit} data-testid="cpd-apply-form">
            <Card className="imcac-card mb-6">
              <CardHeader>
                <CardTitle className="text-lg text-[#1B2A4A] font-heading">Organization Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="organization_name">Organization Name *</Label>
                    <Input id="organization_name" name="organization_name" value={formData.organization_name} onChange={handleChange} required className="rounded-sm" data-testid="input-org-name" />
                  </div>
                  <div>
                    <Label htmlFor="contact_person">Contact Person *</Label>
                    <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleChange} required className="rounded-sm" data-testid="input-contact" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="rounded-sm" data-testid="input-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} className="rounded-sm" data-testid="input-phone" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="website">Organization Website</Label>
                  <Input id="website" name="website" value={formData.website} onChange={handleChange} className="rounded-sm" placeholder="https://" data-testid="input-website" />
                </div>
              </CardContent>
            </Card>

            <Card className="imcac-card mb-6">
              <CardHeader>
                <CardTitle className="text-lg text-[#1B2A4A] font-heading">Program Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="program_name">Program / Activity Name *</Label>
                    <Input id="program_name" name="program_name" value={formData.program_name} onChange={handleChange} required className="rounded-sm" data-testid="input-program-name" />
                  </div>
                  <div>
                    <Label htmlFor="program_type">Activity Type</Label>
                    <select id="program_type" name="program_type" value={formData.program_type} onChange={handleChange} className="w-full h-10 px-3 border border-slate-200 rounded-sm text-sm" data-testid="select-program-type">
                      <option value="">Select type</option>
                      <option value="online_course">Online Course</option>
                      <option value="conference">Conference</option>
                      <option value="congress">Congress</option>
                      <option value="workshop">Workshop</option>
                      <option value="symposium">Symposium</option>
                      <option value="fellowship">Fellowship Program</option>
                      <option value="webinar">Webinar</option>
                      <option value="faculty_development">Faculty Development Program</option>
                      <option value="clinical_skills">Clinical Skills Program</option>
                      <option value="healthcare_quality">Healthcare Quality Program</option>
                      <option value="leadership">Leadership Program</option>
                      <option value="digital_health">Digital Health Program</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="learning_hours">Total Learning Hours</Label>
                    <Input id="learning_hours" name="learning_hours" type="number" value={formData.learning_hours} onChange={handleChange} className="rounded-sm" data-testid="input-hours" />
                  </div>
                  <div>
                    <Label htmlFor="recognition_level">Recognition Level Sought</Label>
                    <select id="recognition_level" name="recognition_level" value={formData.recognition_level} onChange={handleChange} className="w-full h-10 px-3 border border-slate-200 rounded-sm text-sm" data-testid="select-level">
                      <option value="level_1">Level 1 — IMCAC CPD Recognized</option>
                      <option value="level_2">Level 2 — IMCAC CPD Quality Assured</option>
                      <option value="level_3">Level 3 — IMCAC Advanced CPD Recognized</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="target_audience">Target Audience</Label>
                  <Input id="target_audience" name="target_audience" value={formData.target_audience} onChange={handleChange} className="rounded-sm" placeholder="e.g., Medical Doctors, Nurses, Allied Health" data-testid="input-audience" />
                </div>
                <div>
                  <Label htmlFor="program_description">Program Description *</Label>
                  <Textarea id="program_description" name="program_description" value={formData.program_description} onChange={handleChange} required rows={4} className="rounded-sm" placeholder="Brief description of the educational activity..." data-testid="input-description" />
                </div>
                <div>
                  <Label htmlFor="learning_objectives">Learning Objectives</Label>
                  <Textarea id="learning_objectives" name="learning_objectives" value={formData.learning_objectives} onChange={handleChange} rows={3} className="rounded-sm" placeholder="List the key learning objectives..." data-testid="input-objectives" />
                </div>
              </CardContent>
            </Card>

            <Button type="submit" disabled={submitting} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm py-3" data-testid="submit-application-btn">
              {submitting ? 'Submitting...' : <><Send className="h-4 w-4 mr-2" /> Submit Application</>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
